// W32 (2026-10-20): one identity hook for the six customer sub-tabs
// (Build an order, Subscription, Colic Coverage, Gifts, Horses,
// Relationships).
//
//   const identity = useReviewIdentity(customerId)
//
//   identity.isPreview   true ONLY when useContactProfile reports
//                        status 'skipped' (explicit ?preview= or the dev
//                        mock session). Every other status, including
//                        loading / error / empty, is a real session and
//                        the screen must render real data or an honest
//                        placeholder, never the SARAH / MICHAEL / DUKE /
//                        JAMIE fixture (W26 hard rule).
//   identity.customer    MockCustomer-shaped record: SARAH in preview,
//                        the real HubSpot profile when ready, an honest
//                        placeholder while loading / on error / on 404.
//   identity.firstName   display-safe first name ('this customer' until
//                        the profile is ready).
//   identity.owner       PriorityFocus owner: the HubSpot contact owner
//                        or an explicit "Unassigned". Never JAMIE.
//
// The derivation mirrors screens/CurrentActionReview/index.tsx
// (activeCustomer + deriveRealHero owner branch). That file is owned by
// W27, so the mapping is repeated here rather than imported; W38 can
// point Current Action at this hook once both waves have merged.
//
// useContactAddresses() lives here too: Build an order needs the
// HubSpot default address (GET /v1/contacts/:id/addresses) and the W26
// collection helper in useContactCollections.ts is not exported.

import { useEffect, useMemo, useRef, useState } from 'react';

import type { MockCustomer } from '../../data/mockData.js';
import { SARAH } from '../../data/mockData.js';
import type { PriorityOwner } from './PriorityFocus.js';
import {
  buildSessionHeaders,
  useContactProfile,
  useContactReadGate,
  type ContactProfileRecord,
  type ContactProfileStatus,
} from './useContactProfile.js';

export const UNASSIGNED_OWNER: PriorityOwner = { initials: 'UN', fullName: 'Unassigned' };

export interface ReviewIdentity {
  status: ContactProfileStatus;
  isPreview: boolean;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  contact: ContactProfileRecord | null;
  customer: MockCustomer;
  firstName: string;
  owner: PriorityOwner;
  ownerDetail: string;
  /** Identity-row badge: lifecycle stage on a real contact. */
  badgeLabel: string;
  /** Identity-row second line on a real contact; undefined keeps the preview copy. */
  sinceLabel: string | undefined;
  /** Breadcrumb status pill text for a real session; null when nothing to say. */
  statusPill: string | null;
}

function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function initialsFor(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const out = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
  return out || '??';
}

function monthYear(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Map the real profile onto the MockCustomer shape the shared chrome renders. */
export function customerFromProfile(c: ContactProfileRecord): MockCustomer {
  return {
    id: c.id,
    initials: c.initials || initialsFor(c.fullName),
    firstName: c.firstName || c.fullName.split(' ')[0] || c.id,
    fullName: c.fullName,
    customerSince: monthYear(c.createdAt) ?? 'date unknown',
    city: c.city || '',
    stateCode: c.state || '',
    localTime: '',
    phone: c.phone || '',
    email: c.email || '',
    // W38: real HubSpot `preferred_channel`, '' renders as "not set".
    preferredChannel: typeof c.preferredChannel === 'string' ? c.preferredChannel.trim() : '',
  };
}

function placeholderCustomer(customerId: string, status: ContactProfileStatus): MockCustomer {
  const label =
    status === 'loading'
      ? 'Loading contact…'
      : status === 'empty'
        ? `No HubSpot contact ${customerId}`
        : 'Contact could not load';
  return {
    id: customerId,
    initials: customerId.slice(0, 2).toUpperCase(),
    firstName: '',
    fullName: label,
    customerSince: 'date unknown',
    city: '',
    stateCode: '',
    localTime: '',
    phone: '',
    email: '',
    preferredChannel: '',
  };
}

export function useReviewIdentity(customerId: string): ReviewIdentity {
  const profile = useContactProfile(customerId);
  return useMemo<ReviewIdentity>(() => {
    const isPreview = profile.status === 'skipped';
    if (isPreview) {
      return {
        status: profile.status,
        isPreview: true,
        loading: false,
        error: null,
        errorCode: null,
        contact: null,
        customer: SARAH,
        firstName: SARAH.firstName,
        // Preview keeps the PriorityFocus default (JAMIE) by passing no owner.
        owner: UNASSIGNED_OWNER,
        ownerDetail: 'Primary account owner. Coverage preserves ownership.',
        badgeLabel: 'Active subscriber',
        sinceLabel: undefined,
        statusPill: 'Design preview · Sample customer',
      };
    }
    const ready = profile.status === 'ready' && !!profile.contact;
    const c = profile.contact;
    const customer = ready && c ? customerFromProfile(c) : placeholderCustomer(customerId, profile.status);
    const owner: PriorityOwner = c?.ownerName
      ? { initials: initialsFor(c.ownerName), fullName: c.ownerName }
      : UNASSIGNED_OWNER;
    const statusPill =
      profile.status === 'loading'
        ? 'Loading contact…'
        : profile.status === 'error'
          ? `Contact load failed (${profile.errorCode || 'ERR'})`
          : profile.status === 'empty'
            ? `No HubSpot contact for id ${customerId}`
            : null;
    return {
      status: profile.status,
      isPreview: false,
      loading: profile.status === 'loading',
      error: profile.status === 'error' ? profile.error ?? 'Contact could not load' : null,
      errorCode: profile.status === 'error' ? profile.errorCode ?? 'UNKNOWN' : null,
      contact: c,
      customer,
      firstName: ready && customer.firstName ? customer.firstName : 'this customer',
      owner,
      ownerDetail: c?.ownerName ? 'HubSpot contact owner.' : 'No HubSpot owner on this contact yet.',
      badgeLabel: c?.lifecycleStage ? titleCase(c.lifecycleStage) : 'Contact',
      sinceLabel: c?.lastCallDate
        ? `Last VanillaSoft call ${c.lastCallDate}`
        : c?.createdAt
          ? `In HubSpot since ${monthYear(c.createdAt)}`
          : ' ',
      statusPill,
    };
  }, [profile, customerId]);
}

// --- Addresses -----------------------------------------------------------

/** Shape of one entry in GET /v1/contacts/:id/addresses (MEASURED 2026-10-20). */
export interface ContactAddressRecord {
  addrId: string | null;
  source: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

export type AddressesStatus = 'loading' | 'ready' | 'error' | 'skipped';

export interface ContactAddressesResult {
  status: AddressesStatus;
  items: ContactAddressRecord[];
  errorCode?: string;
}

export function useContactAddresses(customerId: string | null | undefined): ContactAddressesResult {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const [state, setState] = useState<ContactAddressesResult>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    items: [],
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId || !gate.session) {
      setState({ status: 'skipped', items: [] });
      return;
    }
    const requestId = `${customerId}:addresses:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', items: [] });
    const headers = buildSessionHeaders(gate.session);
    const url = `/v1/contacts/${encodeURIComponent(customerId)}/addresses`;
    fetch(url, { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          try {
            const parsed = JSON.parse(await response.text()) as { errorCode?: string };
            if (parsed && typeof parsed.errorCode === 'string') errorCode = parsed.errorCode;
          } catch {
            /* non-JSON or empty error body */
          }
          setState({ status: 'error', items: [], errorCode });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({ status: 'error', items: [], errorCode: 'BAD_JSON' });
          return;
        }
        const list = body && typeof body === 'object'
          ? (body as Record<string, unknown>)['addresses']
          : null;
        setState({ status: 'ready', items: Array.isArray(list) ? (list as ContactAddressRecord[]) : [] });
      })
      .catch(() => {
        if (latest.current !== requestId) return;
        setState({ status: 'error', items: [], errorCode: 'FETCH_FAILED' });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, sessionIdentity, sessionToken]);

  return state;
}
